import React, { Component } from "react";
import {
  Table,
  Row,
  Col,
  Button,
  Spinner,
  Modal,
  Form,
  FormControl,
} from "react-bootstrap";
import CartContext from "../StoreManager/CartContext";
import { fetchCrmList, saveCrmItem, deleteCrmItem } from "./CrmApi";

const emptyRole = {
  roleId: 0,
  storeId: "",
  roleName: "",
  description: "",
};

export default class CrmRoles extends Component {
  static contextType = CartContext;

  constructor(props) {
    super(props);
    this.state = {
      roles: [],
      filteredRoles: [],
      loading: true,
      search: "",
      error: null,
      showModal: false,
      isEdit: false,
      currentRole: { ...emptyRole },
    };
  }

  componentDidMount() {
    this.loadRoles();
  }

  loadRoles = async () => {
    const storeId = this.context.storeId;
    if (!storeId) {
      this.setState({ loading: false, error: "StoreId is required." });
      return;
    }

    this.setState({ loading: true, error: null });
    try {
      const roles = await fetchCrmList("Roles", "GetRoles", storeId);
      const normalized = Array.isArray(roles) ? roles : [];
      this.setState({
        roles: normalized,
        filteredRoles: normalized,
        loading: false,
      });
    } catch (error) {
      this.setState({
        loading: false,
        error: error.message || "Unable to load roles.",
      });
    }
  };

  onSearchChange = (event) => {
    const search = event.target.value || "";
    const normalizedSearch = search.trim().toLowerCase();
    const filteredRoles = this.state.roles.filter((role) => {
      const name = (role.roleName || "").toLowerCase();
      const description = (role.description || "").toLowerCase();
      return (
        name.includes(normalizedSearch) ||
        description.includes(normalizedSearch)
      );
    });
    this.setState({ search, filteredRoles });
  };

  handleShowModal = (role = null) => {
    this.setState({
      currentRole: role ? { ...role } : { ...emptyRole },
      isEdit: Boolean(role),
      showModal: true,
      error: null,
    });
  };

  handleCloseModal = () => {
    this.setState({ showModal: false, error: null });
  };

  handleFieldChange = (field, value) => {
    this.setState((prevState) => ({
      currentRole: {
        ...prevState.currentRole,
        [field]: value,
      },
    }));
  };

  saveRole = async () => {
    const storeId = this.context.storeId;
    if (!storeId) {
      this.setState({ error: "StoreId is required." });
      return;
    }

    const role = {
      ...this.state.currentRole,
      storeId,
      roleId: Number(this.state.currentRole.roleId) || 0,
    };

    if (!role.roleName) {
      this.setState({ error: "Role name is required." });
      return;
    }

    try {
      await saveCrmItem("Roles", "AddOrUpdateRole", role);
      await this.loadRoles();
      this.handleCloseModal();
    } catch (error) {
      this.setState({ error: error.message || "Unable to save role." });
    }
  };

  deleteRole = async (roleId) => {
    const storeId = this.context.storeId;
    if (!storeId) return;
    if (!window.confirm("Delete this role?")) return;

    try {
      await deleteCrmItem("Roles", "DeleteRole", storeId, roleId);
      await this.loadRoles();
    } catch (error) {
      this.setState({ error: error.message || "Unable to delete role." });
    }
  };

  render() {
    const {
      filteredRoles,
      loading,
      search,
      error,
      showModal,
      isEdit,
      currentRole,
    } = this.state;
    const storeId = this.context.storeId;

    return (
      <div className="m-3">
        <Row className="align-items-center mb-3">
          <Col>
            <h2>CRM Roles</h2>
            <p className="text-muted">
              Manage CRM roles for the selected store.
            </p>
          </Col>
          <Col xs="auto" className="d-flex gap-2">
            <Button variant="primary" onClick={() => this.handleShowModal()}>
              Add Role
            </Button>
            <Button
              variant="secondary"
              onClick={this.loadRoles}
              disabled={loading}
            >
              {loading ? <Spinner animation="border" size="sm" /> : "Refresh"}
            </Button>
          </Col>
        </Row>

        {!storeId && (
          <div className="alert alert-warning">
            StoreId is required to load CRM roles.
          </div>
        )}
        {error && <div className="alert alert-danger">{error}</div>}

        <Row className="mb-3">
          <Col md={6} lg={4}>
            <FormControl
              placeholder="Search by role or description"
              value={search}
              onChange={this.onSearchChange}
            />
          </Col>
        </Row>

        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Role</th>
              <th>Description</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="3" className="text-center py-5">
                  <Spinner animation="border" /> Loading...
                </td>
              </tr>
            ) : filteredRoles.length === 0 ? (
              <tr>
                <td colSpan="3" className="text-center py-5">
                  No roles found.
                </td>
              </tr>
            ) : (
              filteredRoles.map((role) => (
                <tr key={role.roleId || Math.random()}>
                  <td>{role.roleName || "-"}</td>
                  <td>{role.description || "-"}</td>
                  <td>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      className="me-2"
                      onClick={() => this.handleShowModal(role)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => this.deleteRole(role.roleId)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>

        <Modal show={showModal} onHide={this.handleCloseModal}>
          <Modal.Header closeButton>
            <Modal.Title>{isEdit ? "Edit Role" : "Add Role"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3" controlId="roleName">
                <Form.Label>Role Name</Form.Label>
                <Form.Control
                  type="text"
                  value={currentRole.roleName}
                  onChange={(e) =>
                    this.handleFieldChange("roleName", e.target.value)
                  }
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="description">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={currentRole.description}
                  onChange={(e) =>
                    this.handleFieldChange("description", e.target.value)
                  }
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={this.handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" onClick={this.saveRole}>
              Save
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}
